/**
 * SSR-safe localStorage helpers. The web app is client-only, but importing
 * the store from a test harness or any non-browser tool would otherwise blow
 * up on `localStorage is not defined`. These wrappers fall back to in-memory
 * defaults silently when `window` isn't available.
 */

function hasWindow(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function safeGet(key: string): string | null {
  if (!hasWindow()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function safeSet(key: string, value: string): void {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Quota exceeded / privacy-mode Safari — silently ignore.
  }
}

export function safeGetJson<T>(key: string, fallback: T): T {
  const raw = safeGet(key);
  if (raw == null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function safeSetJson(key: string, value: unknown): void {
  try {
    safeSet(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export function safeGetBool(key: string): boolean {
  return safeGet(key) === "1";
}

export function safeSetBool(key: string, value: boolean): void {
  safeSet(key, value ? "1" : "0");
}
